import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Badge } from "@/shared/ui/badge";
import {
  Plus,
  Check,
  Clock,
  Trash2,
  Calendar,
  User,
  AlertCircle,
  CheckCircle2,
  Circle,
  Loader2,
  Mail,
  Send
} from "lucide-react";
import { format, isPast, isToday } from "date-fns";
import { toast } from "sonner";
import {
  fetchActionsByClaimAndStep,
  fetchClaimForAction,
  createActionForClaim,
  updateActionStatus,
  deleteAction,
  Action,
  ClaimForAction,
} from "../api/actions";
import { createSupplierPortalClient, type SupplierClaim } from "../api/supplier-portal";

interface ActionManagerProps {
  claimId: string;
  stepNumber: number;
  title?: string;
  supplierToken?: string;
  claimForNotification?: SupplierClaim;
}

const statusConfig = {
  pending: { 
    label: "Pending", 
    icon: Circle, 
    className: "bg-muted/50 text-muted-foreground border-muted" 
  },
  in_progress: { 
    label: "In Progress", 
    icon: Clock, 
    className: "bg-warning/20 text-warning border-warning/30" 
  },
  completed: { 
    label: "Done", 
    icon: CheckCircle2, 
    className: "bg-success/20 text-success border-success/30" 
  },
};

const stepTitles: Record<number, string> = {
  3: "D3 - Containment Actions",
  5: "D5 - Permanent Corrective Actions",
  6: "D6 - Implement & Validate",
};

const isValidEmail = (value: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(value);
};

export function ActionManager({
  claimId,
  stepNumber,
  title = "Actions",
  supplierToken,
  claimForNotification,
}: ActionManagerProps) {
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [newAction, setNewAction] = useState({ description: "", responsible: "", due_date: "" });
  const [sendingNotification, setSendingNotification] = useState<string | null>(null);
  const supplierClient = supplierToken ? createSupplierPortalClient(supplierToken) : null;

  // Fetch claim data for magic link
  const { data: claim } = useQuery({
    queryKey: ["mod_8d_claim_for_action", claimId],
    queryFn: () => fetchClaimForAction(claimId),
    enabled: !claimForNotification && !supplierClient,
  });

  const { data: actions, isLoading } = useQuery({
    queryKey: ["mod_8d_actions", claimId, stepNumber],
    queryFn: () => supplierClient?.fetchActions(stepNumber) ?? fetchActionsByClaimAndStep(claimId, stepNumber),
  });

  const sendActionNotification = async (
    claim: ClaimForAction | SupplierClaim,
    action: { description: string; responsible: string; due_date: string }
  ) => {
    if (!isValidEmail(action.responsible)) return;

    const magicLink = supplierToken
      ? `https://app.factory-pilot.ai/8d/supplier/${claim.id}?token=${supplierToken}`
      : `https://app.factory-pilot.ai/8d/supplier/${claim.id}?token=${(claim as ClaimForAction).magic_token}`;
    
    try {
      const response = await supabase.functions.invoke("send-action-notification", {
        body: {
          to: action.responsible,
          actionDescription: action.description,
          claimNumber: claim.claim_number,
          claimTitle: claim.title,
          stepTitle: stepTitles[stepNumber] || `Step ${stepNumber}`,
          dueDate: action.due_date || null,
          magicLink,
          factoryName: claim.factory?.name || "Factory",
        },
      });

      if (response.error) {
        console.error("Failed to send notification:", response.error);
        toast.error("Failed to send email notification");
      } else {
        toast.success(`Notification sent to ${action.responsible}`);
      }
    } catch (error) {
      console.error("Error sending notification:", error);
    }
  };

  const addMutation = useMutation({
    mutationFn: async () => {
      if (supplierClient) {
        await supplierClient.createAction({
          step_number: stepNumber,
          description: newAction.description,
          responsible: newAction.responsible || null,
          due_date: newAction.due_date || null,
        });
      } else {
        await createActionForClaim(claimId, stepNumber, {
          description: newAction.description,
          responsible: newAction.responsible || null,
          due_date: newAction.due_date || null,
        });
      }
      
      // Send notification if responsible is an email
      const notificationClaim = claimForNotification ?? claim;
      if (notificationClaim && isValidEmail(newAction.responsible)) {
        await sendActionNotification(notificationClaim, newAction);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mod_8d_actions", claimId, stepNumber] });
      setNewAction({ description: "", responsible: "", due_date: "" });
      setIsAdding(false);
      toast.success("Action added");
    },
    onError: () => toast.error("Failed to add action"),
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      if (supplierClient) {
        await supplierClient.updateActionStatus(id, status);
      } else {
        await updateActionStatus(id, status);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mod_8d_actions", claimId, stepNumber] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (supplierClient) {
        await supplierClient.deleteAction(id);
      } else {
        await deleteAction(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mod_8d_actions", claimId, stepNumber] });
      toast.success("Action deleted");
    },
  });

  const resendNotification = async (action: Action) => {
    const notificationClaim = claimForNotification ?? claim;
    if (!notificationClaim || !action.responsible || !isValidEmail(action.responsible)) return;
    
    setSendingNotification(action.id);
    await sendActionNotification(notificationClaim, {
      description: action.description,
      responsible: action.responsible,
      due_date: action.due_date || "",
    });
    setSendingNotification(null);
  };

  const cycleStatus = (action: Action) => {
    const statusOrder = ["pending", "in_progress", "completed"];
    const currentIndex = statusOrder.indexOf(action.status);
    const nextStatus = statusOrder[(currentIndex + 1) % statusOrder.length];
    updateStatusMutation.mutate({ id: action.id, status: nextStatus });
  };

  const completedCount = actions?.filter(a => a.status === "completed").length || 0;
  const totalCount = actions?.length || 0;
  const progress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with progress */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h4 className="font-medium text-sm">{title}</h4>
          {totalCount > 0 && (
            <Badge variant="outline" className="text-xs">
              {completedCount}/{totalCount} complete
            </Badge>
          )}
        </div>
        {totalCount > 0 && (
          <div className="flex items-center gap-2">
            <div className="w-24 h-1.5 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-success transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="text-xs text-muted-foreground">{progress}%</span>
          </div>
        )}
      </div>

      {/* Actions list */}
      <div className="space-y-2">
        {actions?.map((action) => {
          const config = statusConfig[action.status as keyof typeof statusConfig] || statusConfig.pending;
          const StatusIcon = config.icon;
          const isOverdue = action.due_date && isPast(new Date(action.due_date)) && action.status !== "completed";
          const isDueToday = action.due_date && isToday(new Date(action.due_date));
          const hasEmail = action.responsible && isValidEmail(action.responsible);

          return (
            <div
              key={action.id}
              className={`group flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                action.status === "completed" 
                  ? "bg-success/5 border-success/20" 
                  : isOverdue
                    ? "bg-destructive/5 border-destructive/20"
                    : "bg-secondary/30 border-border hover:bg-secondary/50"
              }`}
            >
              {/* Status toggle button */}
              <button
                onClick={() => cycleStatus(action)}
                className={`mt-0.5 flex-shrink-0 p-1 rounded-full transition-colors ${config.className}`}
                title={`Status: ${config.label} (click to change)`}
              >
                <StatusIcon className="h-4 w-4" />
              </button>

              {/* Content */}
              <div className="flex-1 min-w-0 space-y-1">
                <p className={`text-sm ${action.status === "completed" ? "line-through text-muted-foreground" : ""}`}>
                  {action.description}
                </p>
                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                  {action.responsible && (
                    <span className={`flex items-center gap-1 ${hasEmail ? "text-chart-2" : ""}`}>
                      {hasEmail ? <Mail className="h-3 w-3" /> : <User className="h-3 w-3" />}
                      {action.responsible}
                    </span>
                  )}
                  {action.due_date && (
                    <span className={`flex items-center gap-1 ${
                      isOverdue ? "text-destructive" : isDueToday ? "text-warning" : ""
                    }`}>
                      {isOverdue && <AlertCircle className="h-3 w-3" />}
                      <Calendar className="h-3 w-3" />
                      {format(new Date(action.due_date), "MMM d")}
                      {isOverdue && " (overdue)"}
                    </span>
                  )}
                  {action.completed_at && (
                    <span className="flex items-center gap-1 text-success">
                      <Check className="h-3 w-3" />
                      {format(new Date(action.completed_at), "MMM d")}
                    </span>
                  )}
                </div>
              </div>

              {/* Resend notification button for email addresses */}
              {hasEmail && action.status !== "completed" && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-chart-2"
                  onClick={() => resendNotification(action)}
                  disabled={sendingNotification === action.id}
                  title="Resend notification"
                >
                  {sendingNotification === action.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Send className="h-3.5 w-3.5" />
                  )}
                </Button>
              )}

              {/* Delete button */}
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                onClick={() => deleteMutation.mutate(action.id)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          );
        })}

        {actions?.length === 0 && !isAdding && (
          <p className="text-sm text-muted-foreground italic text-center py-4">
            No actions defined yet
          </p>
        )}
      </div>

      {/* Add new action form */}
      {isAdding ? (
        <div className="space-y-3 p-4 rounded-lg border border-primary/30 bg-primary/5">
          <Input
            placeholder="Action description..."
            value={newAction.description}
            onChange={(e) => setNewAction(prev => ({ ...prev, description: e.target.value }))}
            autoFocus
          />
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <div className="relative">
                {isValidEmail(newAction.responsible) ? (
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-chart-2" />
                ) : (
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                )}
                <Input
                  placeholder="Name or email address"
                  value={newAction.responsible}
                  onChange={(e) => setNewAction(prev => ({ ...prev, responsible: e.target.value }))}
                  className="pl-9"
                />
              </div>
              {isValidEmail(newAction.responsible) && (
                <p className="text-xs text-chart-2 flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  Notification will be sent
                </p>
              )}
            </div>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="date"
                value={newAction.due_date}
                onChange={(e) => setNewAction(prev => ({ ...prev, due_date: e.target.value }))}
                className="pl-9"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              size="sm" 
              onClick={() => addMutation.mutate()}
              disabled={!newAction.description.trim() || addMutation.isPending}
            >
              {addMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Action"}
            </Button>
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={() => {
                setIsAdding(false);
                setNewAction({ description: "", responsible: "", due_date: "" });
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="w-full border-dashed"
          onClick={() => setIsAdding(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Action
        </Button>
      )}
    </div>
  );
}
